import { useActivities } from '../activity/useActivities'
import { useActivityLogs } from '../activity/useActivityLogs'
import useAnnouncements from '../announcements/useAnnouncements'
import { useChallengeContext } from '../challenge/useChallengeContext'
import { useChallengeAccess } from '../membership/useChallengeAccess'
import { useChallengeId } from '../challenge/useChallengeId'
import { useCheckpoints } from '../checkpoint/useCheckpoints'
import { useRestDays } from '../restDays/useRestDays'
import { useTeam } from '../team/useTeam'
import { useUser } from '../user/useUser'
import ActivityLogic from '../../logic/ActivityLogic'
import {
  checkpointBarPosition,
  resolveCheckpoints,
  type ResolvedCheckpoint,
} from '../../logic/checkpointUtils'
import { getRouteCoordinates, type RouteCoordinate } from '../../logic/routeUtils'
import type Activity from '../../model/Activity'
import ActivityLog from '../../model/ActivityLog'
import type ChallengeBucket from '../../model/ChallengeBucket'
import type { ProgressType } from '../../model/Challenge'
import User from '../../model/User'
import { buildActivityFeed, type ActivityFeedItem } from '../../logic/activityFeed'
import { bucketDisplayLabel, getBucketDisplayInfo } from '../../util/ActivityIconUtil'

export interface BucketSummary {
  bucketId: string
  bucketName: string
  label: string
  icon: string
  value: number
  goal: number
  percent: number
  variant: 'default' | 'done' | 'warn'
}

export interface MemberSummary {
  user: User
  role: 'you' | 'leader' | 'teammate'
  totalRms: number
  teamPercent: number
  buckets: { label: string; value: number; teamPercent: number }[]
}

export interface HomeData {
  isLoading: boolean
  teamName: string
  currentDay: number
  totalDays: number
  teamProgress: number
  goal: number
  progressType: ProgressType
  routeCoordinates: RouteCoordinate[]
  progressPercent: number
  paceProgress: number
  paceDelta: number
  dailyPace: number
  checkpoints: ResolvedCheckpoint[]
  gateMarks: { position: number; status: 'done' | 'next' | 'pending' }[]
  buckets: BucketSummary[]
  restDaysUsed: number
  restDaysTotal: number
  restDaysRemaining: number
  members: MemberSummary[]
  feed: ActivityFeedItem[]
  announcement?: { id: string; title: string; link?: string; videoUrl?: string }
}

const RECENT_ACTIVITY_LIMIT = 4

function bucketVariant(percent: number): 'default' | 'done' | 'warn' {
  if (percent >= 100) return 'done'
  if (percent < 75) return 'warn'
  return 'default'
}

function buildMembers(
  members: User[],
  currentUserId: string,
  leaderId: string,
  activityLogs: ActivityLog[],
  activities: Activity[],
  teamProgress: number,
  buckets: ChallengeBucket[],
): MemberSummary[] {
  return [...members]
    .map((user) => {
      const totalRms = ActivityLogic.getIndividualProgress(activityLogs, user.id)
      const teamPercent = teamProgress > 0 ? (totalRms / teamProgress) * 100 : 0
      const role: MemberSummary['role'] =
        user.id === currentUserId ? 'you' : user.id === leaderId ? 'leader' : 'teammate'

      const memberBuckets = buckets.map((bucket) => {
        const value = ActivityLogic.getBucketProgress(activityLogs, activities, bucket, user.id)
        const teamBucket = ActivityLogic.getBucketProgress(activityLogs, activities, bucket)
        return {
          label: bucketDisplayLabel(bucket),
          value: Math.round(value),
          teamPercent: teamBucket > 0 ? Math.round((value / teamBucket) * 100) : 0,
        }
      })

      return {
        user,
        role,
        totalRms: Math.round(totalRms),
        teamPercent: Math.round(teamPercent),
        buckets: memberBuckets,
      }
    })
    .sort((a, b) => b.totalRms - a.totalRms)
}

export function useHomeData(): HomeData | null {
  const { data: user } = useUser()
  const { teamID, isFreeAgent } = useChallengeAccess()
  const { count: restDaysUsed, remaining: restDaysRemaining } = useRestDays()
  const { data: team, isLoading: teamLoading } = useTeam(teamID)
  const { data: challengeContext, isLoading: challengeLoading } = useChallengeContext()
  const { data: activities, isLoading: activitiesLoading } = useActivities()
  const { data: activityLogs, isLoading: logsLoading } = useActivityLogs()
  const { data: checkpointsData, isLoading: checkpointsLoading } = useCheckpoints()
  const challengeId = useChallengeId()
  const { data: announcements } = useAnnouncements(challengeId)

  if (!user || !teamID || isFreeAgent) return null
  if (
    teamLoading ||
    challengeLoading ||
    activitiesLoading ||
    logsLoading ||
    checkpointsLoading
  ) {
    return null
  }
  if (!team || !challengeContext || !activities || !activityLogs || !checkpointsData?.length) {
    return null
  }

  const { challenge, buckets, goal, restDaysRequired } = challengeContext
  const teamProgress = ActivityLogic.getTotalProgress(activityLogs)
  const progressPercent = (teamProgress / goal) * 100
  const totalDays = challenge.getTotalDays()
  const currentDay = Math.max(1, totalDays - challenge.getDaysRemaining() + 1)
  const paceProgress = challenge.getPaceProgress()
  const paceDelta = Math.round(teamProgress - paceProgress)
  const dailyPace = Math.round(goal / Math.max(totalDays, 1))

  const checkpoints = resolveCheckpoints(checkpointsData, teamProgress, goal)
  const routeCoordinates = getRouteCoordinates(checkpointsData)
  const gateMarks: HomeData['gateMarks'] = checkpoints
    .filter((cp) => !cp.isFinish)
    .map((cp) => ({
      position: checkpointBarPosition(cp.rms, goal),
      status: cp.status === 'done' ? 'done' : cp.status === 'next' ? 'next' : 'pending',
    }))

  const bucketSummaries: BucketSummary[] = buckets.map((bucket) => {
    const value = ActivityLogic.getBucketProgress(activityLogs, activities, bucket)
    const percent = (value / bucket.goal) * 100
    const { icon } = getBucketDisplayInfo(bucket)
    return {
      bucketId: bucket.id,
      bucketName: bucket.name,
      label: bucketDisplayLabel(bucket),
      icon,
      value: Math.round(value),
      goal: bucket.goal,
      percent,
      variant: bucketVariant(percent),
    }
  })

  const members = buildMembers(
    team.members,
    user.id,
    team.leaderID,
    activityLogs,
    activities,
    teamProgress,
    buckets,
  )

  const sortedAnnouncements = [...(announcements ?? [])].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
  )
  const topAnnouncement = sortedAnnouncements[0]

  return {
    isLoading: false,
    teamName: team.name,
    currentDay,
    totalDays,
    teamProgress: Math.round(teamProgress),
    goal,
    progressType: challenge.progressType,
    routeCoordinates,
    progressPercent,
    paceProgress: Math.round(paceProgress),
    paceDelta,
    dailyPace,
    checkpoints,
    gateMarks,
    buckets: bucketSummaries,
    restDaysUsed,
    restDaysTotal: restDaysRequired,
    restDaysRemaining,
    members,
    feed: buildActivityFeed(activityLogs, activities, team.members).slice(0, RECENT_ACTIVITY_LIMIT),
    announcement: topAnnouncement
      ? {
          id: topAnnouncement.id,
          title: topAnnouncement.title,
          link: topAnnouncement.link,
          videoUrl: topAnnouncement.videoUrl,
        }
      : undefined,
  }
}
